// src/hooks/useFieldValidation.ts
import { useState, useCallback, useMemo } from 'react';
import type React from 'react';

/**
 * Границы одного числового поля. Значения берутся 1-в-1 из pydantic-команды (ge / le).
 * - min соответствует ge, max соответствует le
 * - integer: true — запрещает дробную часть (int-поля в pydantic)
 * - optional: true — пустая строка считается валидной (Optional[...] без обязательного значения)
 * - step — для float-полей (например рабочие часы с шагом 0.5)
 */
export interface FieldLimit {
  min?: number;
  max?: number;
  integer?: boolean;
  optional?: boolean;
  step?: number;
  /** Кастомная подпись под полем, когда ошибки нет (заменяет автогенерируемую «min–max»). */
  helper?: string;
}

/** Пара «мин ≤ макс»: оба ключа — поля из того же LIMITS. Ошибка вешается на maxKey. */
export interface PairRule {
  minKey: string;
  maxKey: string;
  /** Сообщение при нарушении. По умолчанию — «Макс должен быть ≥ мин». */
  message?: string;
}

/**
 * Кросс-полевое правило (напр. total ≥ per_account).
 * Возвращает текст ошибки, либо null если всё ок. Ошибка вешается на targetKey.
 * values — актуальные строковые значения формы.
 */
export interface CrossRule {
  targetKey: string;
  validate: (values: Record<string, string>) => string | null;
}

export interface ValidationConfig {
  limits: Record<string, FieldLimit>;
  pairs?: PairRule[];
  cross?: CrossRule[];
}

export type FieldErrors = Record<string, string>;

const isBlank = (raw: string) => raw === undefined || raw === null || raw.trim() === '';

/** Проверка одного значения по его границам. Возвращает текст ошибки или null. */
function checkSingle(raw: string, limit: FieldLimit): string | null {
  if (isBlank(raw)) {
    return limit.optional ? null : 'Обязательное поле';
  }

  // Запятую как десятичный разделитель приводим к точке — частый ввод в RU-локали.
  const normalized = raw.replace(',', '.').trim();
  const num = Number(normalized);

  if (Number.isNaN(num)) {
    return 'Только число';
  }
  if (limit.integer && !Number.isInteger(num)) {
    return 'Только целое число';
  }
  if (limit.min !== undefined && num < limit.min) {
    return `Минимум ${limit.min}`;
  }
  if (limit.max !== undefined && num > limit.max) {
    return `Максимум ${limit.max}`;
  }
  return null;
}

/**
 * Хук валидации числовых полей формы.
 *
 * Идея: значение в стейте формы НЕ режем на лету (не дёргаем курсор при вводе).
 * Показываем ошибку через error/helperText MUI. Жёсткий клэмп в диапазон — на blur.
 *
 * Использование:
 *   const v = useFieldValidation({ limits: LIMITS, pairs: PAIRS, cross: CROSS });
 *   <TextField {...v.fieldProps('durationDays', form.durationDays, update)} label="..." />
 *   ...
 *   disabled={... || v.hasErrors}
 *   onClick={() => { if (!v.validateAll(form)) return; startCampaign(); }}
 */
export function useFieldValidation(config: ValidationConfig) {
  const { limits, pairs = [], cross = [] } = config;
  const [errors, setErrors] = useState<FieldErrors>({});

  /** Полный прогон правил по снимку значений. Чистая функция — не трогает стейт. */
  const computeErrors = useCallback(
    (values: Record<string, any>): FieldErrors => {
      const next: FieldErrors = {};

      // 1. Диапазоны отдельных полей
      for (const key of Object.keys(limits)) {
        const raw = values[key];
        const err = checkSingle(raw === undefined || raw === null ? '' : String(raw), limits[key]);
        if (err) next[key] = err;
      }

      // 2. Пары мин ≤ макс (только если оба поля сами по себе валидны и заполнены)
      for (const { minKey, maxKey, message } of pairs) {
        if (next[minKey] || next[maxKey]) continue;
        const minRaw = values[minKey];
        const maxRaw = values[maxKey];
        if (isBlank(String(minRaw ?? '')) || isBlank(String(maxRaw ?? ''))) continue;
        const min = Number(String(minRaw).replace(',', '.'));
        const max = Number(String(maxRaw).replace(',', '.'));
        if (!Number.isNaN(min) && !Number.isNaN(max) && min > max) {
          next[maxKey] = message ?? 'Макс должен быть ≥ мин';
        }
      }

      // 3. Кросс-полевые правила
      const strValues: Record<string, string> = {};
      for (const k of Object.keys(values)) {
        strValues[k] = values[k] === undefined || values[k] === null ? '' : String(values[k]);
      }
      for (const rule of cross) {
        if (next[rule.targetKey]) continue;
        const err = rule.validate(strValues);
        if (err) next[rule.targetKey] = err;
      }

      return next;
    },
    [limits, pairs, cross]
  );

  /** Провалидировать одно поле (и связанные пары/кросс), обновив стейт ошибок. */
 const validateField = useCallback(
    (key: string, values: Record<string, any>) => {
      setErrors(prev => {
        const fresh = computeErrors(values);
        const next = { ...prev };

        // ключи, которые надо пересчитать: сам key + его пары + кросс-таргеты
        const affected: Record<string, true> = { [key]: true };
        pairs.forEach(p => {
          if (p.minKey === key || p.maxKey === key) {
            affected[p.minKey] = true;
            affected[p.maxKey] = true;
          }
        });
        cross.forEach(c => {
          affected[c.targetKey] = true;
        });

        Object.keys(affected).forEach(k => {
          if (fresh[k]) next[k] = fresh[k];
          else delete next[k];
        });
        return next;
      });
    },
    [computeErrors, pairs, cross]
  );

  /** Полная валидация формы. Возвращает true если ошибок нет. Пишет ошибки в стейт. */
  const validateAll = useCallback(
    (values: Record<string, any>): boolean => {
      const fresh = computeErrors(values);
      setErrors(fresh);
      return Object.keys(fresh).length === 0;
    },
    [computeErrors]
  );

  /** Клэмп значения поля в [min, max] на blur. Возвращает подправленную строку либо исходную. */
  const clampOnBlur = useCallback(
    (key: string, raw: string): string => {
      const limit = limits[key];
      if (!limit || isBlank(raw)) return raw;
      const num = Number(raw.replace(',', '.').trim());
      if (Number.isNaN(num)) return raw;
      let clamped = num;
      if (limit.min !== undefined && clamped < limit.min) clamped = limit.min;
      if (limit.max !== undefined && clamped > limit.max) clamped = limit.max;
      if (limit.integer) clamped = Math.round(clamped);
      return String(clamped);
    },
    [limits]
  );

  /**
   * Готовый набор пропсов для MUI TextField.
   *
   * @param key    ключ поля в LIMITS и в объекте формы
   * @param form   актуальный снимок формы (нужен, чтобы пары/кросс считались сразу при вводе)
   * @param update функция обновления формы (key, value) из компонента
   */
  const fieldProps = useCallback(
    (
      key: string,
      form: Record<string, any>,
      update: (key: any, value: any) => void
    ) => {
      const limit = limits[key] ?? {};
      const value = form[key] ?? '';
      const autoHelper =
        limit.helper ??
        (limit.min !== undefined && limit.max !== undefined
          ? `${limit.min}–${limit.max}`
          : limit.min !== undefined
          ? `Минимум ${limit.min}`
          : limit.max !== undefined
          ? `Максимум ${limit.max}`
          : undefined);

      return {
        value,
        error: !!errors[key],
        helperText: errors[key] || autoHelper,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          const v = e.target.value;
          update(key, v);
          // Считаем по свежему снимку: текущая форма + новое значение поля.
          // Это позволяет паре (min/max) пересчитаться сразу, а не ждать blur.
          validateField(key, { ...form, [key]: v });
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
          const clamped = clampOnBlur(key, e.target.value);
          if (clamped !== e.target.value) {
            update(key, clamped);
            validateField(key, { ...form, [key]: clamped });
          } else {
            validateField(key, { ...form, [key]: e.target.value });
          }
        },
        inputProps: {
          ...(limit.min !== undefined ? { min: limit.min } : {}),
          ...(limit.max !== undefined ? { max: limit.max } : {}),
          ...(limit.step !== undefined ? { step: limit.step } : {}),
        },
      };
    },
    [errors, limits, validateField, clampOnBlur]
  );

  /**
   * Адаптер для форм БЕЗ единого form-объекта, где каждое числовое поле — свой
   * useState<number> с индивидуальным сеттером (напр. NeuroCommenting).
   *
   * Отличия от fieldProps:
   *  - value приходит числом и остаётся числом в стейте;
   *  - при вводе валидируем строку из input, но в стейт кладём число;
   *  - на blur: пустое/невалидное → откатываем к defaultValue (как старое `Number(x)||def`),
   *    иначе клэмпим в [min,max]; всё это тем же механизмом ошибок.
   *
   * Использование:
   *   <TextField {...v.numberFieldProps('minMessageLength', minMessageLength, setMinMessageLength, 20)} />
   */
  const numberFieldProps = useCallback(
    (
      key: string,
      value: number,
      setValue: (v: number) => void,
      defaultValue: number
    ) => {
      const limit = limits[key] ?? {};
      const autoHelper =
        limit.helper ??
        (limit.min !== undefined && limit.max !== undefined
          ? `${limit.min}–${limit.max}`
          : undefined);

      return {
        value,
        type: 'number' as const,
        error: !!errors[key],
        helperText: errors[key] || autoHelper,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          const raw = e.target.value;
          // Валидируем ровно то, что в поле (включая пустоту), но в стейт кладём число.
          // Пустое поле не считаем ошибкой на лету — откат к дефолту произойдёт на blur.
          const num = Number(raw);
          setValue(Number.isNaN(num) ? defaultValue : num);
          validateField(key, { [key]: raw === '' ? String(defaultValue) : raw });
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
          const raw = e.target.value;
          if (raw === '' || Number.isNaN(Number(raw))) {
            setValue(defaultValue);
            validateField(key, { [key]: String(defaultValue) });
            return;
          }
          const clampedStr = clampOnBlur(key, raw);
          setValue(Number(clampedStr));
          validateField(key, { [key]: clampedStr });
        },
        inputProps: {
          ...(limit.min !== undefined ? { min: limit.min } : {}),
          ...(limit.max !== undefined ? { max: limit.max } : {}),
          ...(limit.step !== undefined ? { step: limit.step } : {}),
        },
      };
    },
    [errors, limits, validateField, clampOnBlur]
  );

  const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);

  return {
    errors,
    hasErrors,
    fieldProps,
    numberFieldProps,
    validateField,
    validateAll,
    clampOnBlur,
    setErrors,
  };
}